/**
 * Load Stripe.js from CDN once (avoids npm peer conflicts in this repo).
 * Usage: const stripe = await loadStripe(publishableKey)
 */
let stripePromise = null
let stripeKeyUsed = null

const STRIPE_JS_URL = 'https://js.stripe.com/v3/'

const injectScript = () =>
    new Promise((resolve, reject) => {
        if (window.Stripe) {
            resolve(window.Stripe)
            return
        }
        const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`)
        if (existing) {
            existing.addEventListener('load', () => resolve(window.Stripe))
            existing.addEventListener('error', reject)
            return
        }
        const script = document.createElement('script')
        script.src = STRIPE_JS_URL
        script.async = true
        script.onload = () => resolve(window.Stripe)
        script.onerror = () =>
            reject(new Error('Failed to load Stripe.js from CDN'))
        document.head.appendChild(script)
    })

export const loadStripe = async publishableKey => {
    if (!publishableKey) {
        throw new Error('Stripe publishable key is missing')
    }
    if (stripePromise && stripeKeyUsed === publishableKey) {
        return stripePromise
    }
    stripeKeyUsed = publishableKey
    stripePromise = injectScript().then(StripeCtor => {
        if (!StripeCtor) {
            throw new Error('Stripe.js loaded but Stripe is undefined')
        }
        return StripeCtor(publishableKey)
    })
    return stripePromise
}

/** Prefer ServicePlan StripeClientKey / PurchaseOrder fields; then env. */
export const resolveStripePublishableKey = (apiKeyOrOrder = '') => {
    if (typeof apiKeyOrOrder === 'string' && apiKeyOrOrder) {
        return apiKeyOrOrder
    }

    const o = apiKeyOrOrder && typeof apiKeyOrOrder === 'object' ? apiKeyOrOrder : {}
    const fromApi =
        o.StripeClientKey ||
        o.stripeClientKey ||
        o.StripePublishableKey ||
        o.stripePublishableKey ||
        o.PublishableKey ||
        o.publishableKey ||
        o.ClientKey ||
        o.clientKey ||
        ''
    if (fromApi) return fromApi

    return (
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
        process.env.REACT_APP_STRIPE_CLIENT_KEY ||
        ''
    )
}

/** GET PublicKey may return the key as a string or on a nested object. */
export const publishableKeyFromPublicKey = data => {
    if (typeof data === 'string') {
        return data.startsWith('pk_') ? data : ''
    }
    if (!data || typeof data !== 'object') return ''
    const direct = resolveStripePublishableKey(data)
    if (direct && direct.startsWith('pk_')) return direct
    const nested =
        data.Data ||
        data.data ||
        data.Result ||
        data.result ||
        data.PublicKey ||
        data.publicKey ||
        data.Key ||
        data.key
    if (typeof nested === 'string') {
        return nested.startsWith('pk_') ? nested : ''
    }
    if (nested && typeof nested === 'object') {
        const fromNested = resolveStripePublishableKey(nested)
        return fromNested && fromNested.startsWith('pk_') ? fromNested : ''
    }
    return ''
}

export const paymentFormatNote = data => {
    if (!data || typeof data !== 'object') return ''
    const text =
        data.DisplayMessage ||
        data.displayMessage ||
        data.Message ||
        data.message ||
        data.Format ||
        data.format ||
        ''
    return typeof text === 'string' ? text.trim() : ''
}
